/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;
      this.prefetched = null;

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        } else if (this.dataset.prefetch === 'true') {
          // ZZ: "Mehr laden" mit Prefetch — nächste Seite im Hintergrund
          // vorladen, sobald der Button in die Nähe des Viewports scrollt.
          // Angehängt wird weiterhin erst beim Klick.
          this.prefetchObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.prefetchNext();
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.prefetchObserver.observe(this.pagination);
        }
      }

      // ZZ: Trim & Carry — angebrochene letzte Grid-Reihe verstecken,
      // bis "Mehr laden" geklickt wird (nie auf der letzten Seite).
      this.trimEnabled = this.dataset.trim === 'true' && !this.infiniteScroll;
      if (this.trimEnabled) {
        this.scheduleTrimHandler = this.scheduleTrimHandler || this.scheduleTrim.bind(this);
        window.addEventListener('resize', this.scheduleTrimHandler);

        // Re-Trim, wenn Inhalte ersetzt werden (z. B. Filter-AJAX).
        // Nur childList — applyTrim ändert Attribute/Klassen, löst also
        // keine Endlosschleife aus.
        this.trimObserver = new MutationObserver(this.scheduleTrimHandler);
        this.trimObserver.observe(this, { childList: true, subtree: true });

        this.applyTrim();
      }
    }

    /**
     * ZZ: Debounced re-trim (resize, DOM-Austausch).
     */
    scheduleTrim() {
      clearTimeout(this.trimTimer);
      this.trimTimer = setTimeout(this.applyTrim.bind(this), 150);
    }

    /**
     * ZZ: Versteckt die Elemente der letzten, unvollständigen Grid-Reihe.
     * Beim nächsten Append werden sie automatisch wieder sichtbar (Carry),
     * weil zuerst alle Trims aufgehoben und dann neu berechnet wird.
     */
    applyTrim() {
      if (!this.trimEnabled) return;

      // 1) Bisherige Trims aufheben
      this.querySelectorAll('.js-zz-trimmed').forEach((el) => {
        el.classList.remove('js-zz-trimmed');
        el.removeAttribute('hidden');
      });

      // 2) Letzte Seite (kein "Mehr laden" mehr) → nichts verstecken
      if (!this.querySelector('.js-pagination-load-more')) return;

      const items = Array.from(this.querySelectorAll('.js-pagination-result'))
        .filter((el) => !el.hidden);
      if (items.length < 2) return;

      const grid = items[items.length - 1].parentElement;
      const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      if (cols < 2) return;

      // 3) Letzte Reihe über offsetTop ermitteln (robust auch mit
      //    col-span-full-Promos zwischendrin)
      const lastTop = items[items.length - 1].offsetTop;
      const lastRow = items.filter((el) => Math.abs(el.offsetTop - lastTop) < 3);

      // 4) Unvollständig? → komplette letzte Reihe verstecken
      if (lastRow.length > 0 && lastRow.length < cols) {
        lastRow.forEach((el) => {
          el.classList.add('js-zz-trimmed');
          el.setAttribute('hidden', '');
        });
      }
    }

    /**
     * ZZ: Prefetches the next page of results into memory (no DOM changes).
     * Guarded per button href — filter/sort changes alter the URL and
     * therefore invalidate the cache automatically.
     */
    async prefetchNext() {
      if (!this.loadMoreButton || this.isFetching || this.isPrefetching) return;
      const { href } = this.loadMoreButton;
      if (this.prefetched && this.prefetched.href === href) return;

      this.isPrefetching = true;
      try {
        const response = await fetch(href);
        if (response.ok) {
          this.prefetched = { href, html: await response.text() };
        }
      } catch (error) {
        // still — beim Klick greift dann der normale Fetch
      } finally {
        this.isPrefetching = false;
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.prefetchObserver) this.prefetchObserver.disconnect();
      if (this.trimObserver) this.trimObserver.disconnect();
      if (this.trimTimer) clearTimeout(this.trimTimer);
      if (this.scheduleTrimHandler) window.removeEventListener('resize', this.scheduleTrimHandler);
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve the next page (ZZ: vorgeladene Kopie nutzen, falls vorhanden)
          let pageHtml;
          if (this.prefetched && this.prefetched.href === this.loadMoreButton.href) {
            pageHtml = this.prefetched.html;
          } else {
            const response = await fetch(this.loadMoreButton.href, {
              signal: this.controller.signal
            });

            if (!response.ok) throw new Error(response.status);
            if (this.dataset.pauseInfiniteScroll === 'true') {
              throw new Error('Infinite scroll was interrupted.');
            }

            pageHtml = await response.text();
          }
          this.prefetched = null;

          const tmpl = document.createElement('template');
          tmpl.innerHTML = pageHtml;

          // Append the additional results
          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');

          // Custom event dispatch for Swym
          document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination.dataset.isMoreResults) {
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
            }
          } else {
            this.pagination.remove();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            if (this.prefetchObserver) this.prefetchObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
